/**
 * <tds-subscribe>
 *
 * Reprices the quick view's One-time / Subscribe & Save options when the
 * shopper changes pack size.
 *
 * The radios themselves need no JavaScript: they are named `selling_plan`
 * and sit inside the cart form, which is all /cart/add needs. If this file
 * never loads the shopper can still subscribe — the per-option prices just
 * stop following the pack selection, so the script fails to a stale figure
 * rather than a broken picker.
 *
 * Prices come from the server-rendered table in the snippet, never from
 * Appstle's DOM. That is a standing rule on this theme: after a 6-Pack ->
 * 12-Pack switch the app's widget was observed still showing the 6-Pack
 * amounts, so reading it puts a wrong price on screen, not a stale one.
 *
 * DO NOT read the variant from the form's `[name=id]` inside an event
 * handler. Measured on this theme: `change`, `variant:change`,
 * `variant:updated` and every MutationObserver record all fire while that
 * field still holds the PREVIOUS variant — Flow writes the new value last,
 * as a property, which emits no further signal. Reading it there silently
 * repriced to the variant the shopper just left. The event payload carries
 * the new variant, and the checked swatch is authoritative and synchronous;
 * those are the two sources used here.
 */

class TdsSubscribe extends HTMLElement {
  connectedCallback() {
    this.prices = this.readPrices();
    if (!this.prices) return;

    this.sectionId = this.dataset.sectionId || null;
    this.onVariantEvent = this.onVariantEvent.bind(this);

    this.render(this.resolveVariantId());

    if (window.eventBus) {
      window.eventBus.on('variant:updated', this.onVariantEvent);
      window.eventBus.on('variant:change', this.onVariantEvent);
    }

    /* Backstop for any path that changes the selection without an event we
       hear. It re-resolves from the checked swatch rather than trusting a
       cached id, and render() no-ops when nothing actually changed. */
    const root = this.scope();
    if (root && 'MutationObserver' in window) {
      this.observer = new MutationObserver(() => this.queueRender());
      this.observer.observe(root, { childList: true, subtree: true });
    }
  }

  disconnectedCallback() {
    if (window.eventBus) {
      window.eventBus.off('variant:updated', this.onVariantEvent);
      window.eventBus.off('variant:change', this.onVariantEvent);
    }
    if (this.observer) this.observer.disconnect();
  }

  /* The quick view this picker belongs to; falls back to the whole document
     so a single stray copy still finds its swatches. */
  scope() {
    return this.closest('[data-quick-view-product]') || this.closest('product-information') || document;
  }

  readPrices() {
    const node = this.querySelector('[data-tds-sub-prices]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
  }

  onVariantEvent(payload) {
    const data = (payload && payload.variant ? payload : payload && (payload.details || payload.detail)) || {};

    /* variant:updated names the section it belongs to. Ignore other sections
       so one open quick view never repriced by another form on the page. */
    if (data.sectionId && this.sectionId && data.sectionId !== this.sectionId) return;

    const fromPayload = data.variant && data.variant.id;
    this.render(fromPayload ? String(fromPayload) : this.resolveVariantId());
  }

  /* Checked swatch first — it is correct the moment the shopper clicks.
     The form field is the last resort and only trustworthy outside handlers. */
  resolveVariantId() {
    const root = this.scope();

    const swatches = root.querySelectorAll('.swatch-element[data-swatch-variant-id]');
    for (let i = 0; i < swatches.length; i += 1) {
      const input = swatches[i].querySelector('input');
      if (input && input.checked) return swatches[i].getAttribute('data-swatch-variant-id');
    }

    const form = this.closest('form[action*="/cart/add"]');
    const field = form && form.querySelector('[name="id"]');
    return field ? String(field.value) : null;
  }

  queueRender() {
    if (this.queued) return;
    this.queued = true;
    requestAnimationFrame(() => {
      this.queued = false;
      this.render(this.resolveVariantId());
    });
  }

  render(id) {
    if (!id || id === this.lastId) return;

    const table = this.prices[id];
    if (!table) return;

    this.lastId = id;

    this.querySelectorAll('[data-tds-sub-price]').forEach((el) => {
      const key = el.getAttribute('data-tds-sub-price');
      const price = table[key];
      const option = el.closest('.tds-qvsub__option');

      if (price) {
        el.textContent = price;
        if (option) option.hidden = false;
        return;
      }

      /* A plan this variant has no allocation for cannot be bought; hiding it
         beats offering an option that would fail at checkout. */
      if (option && key !== 'onetime') {
        option.hidden = true;
        const radio = option.querySelector('input[name="selling_plan"]');
        if (radio && radio.checked) {
          const oneTime = this.querySelector('input[name="selling_plan"][value=""]');
          if (oneTime) oneTime.checked = true;
        }
      }
    });
  }
}

if (!customElements.get('tds-subscribe')) {
  customElements.define('tds-subscribe', TdsSubscribe);
}
